import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { getCachedCountries } from "../../../utils/countryStorage";
import type { Country } from "../types";

type CountrySwitcherProps = {
  country: Country;
  onCountryChange: (countryCode: string) => void;
};

export const CountrySwitcher = ({ country, onCountryChange }: CountrySwitcherProps) => {
  const { t } = useTranslation();
  const { isMobile, state } = useSidebar();
  const [open, setOpen] = useState(false);
  const countries = useMemo(() => getCachedCountries() as Country[], []);
  const isCollapsed = state === "collapsed" && !isMobile;

  if (countries.length <= 1) {
    return null;
  }

  const handleSelect = (countryCode?: string) => {
    setOpen(false);

    if (countryCode && countryCode !== country.code) {
      onCountryChange(countryCode);
    }
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <SidebarMenuButton
              size="lg"
              aria-label={t("header.country")}
              className="h-14 gap-3 border border-border px-3 data-[state=open]:bg-primary-100 group-data-[collapsible=icon]:size-10! group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:border-0"
            >
              <span aria-hidden="true" className="text-lg leading-none">
                {country.flag}
              </span>
              <span className="flex min-w-0 flex-col group-data-[collapsible=icon]:hidden">
                <span className="text-muted-foreground text-xs">{t("header.country")}</span>
                <span className="truncate font-semibold text-base">{country.name}</span>
              </span>
              <ChevronsUpDown className="ml-auto text-muted-foreground group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </PopoverTrigger>
          <PopoverContent
            side={isCollapsed ? "right" : "bottom"}
            align="start"
            className="w-(--radix-popover-trigger-width) min-w-60 p-0"
          >
            <Command>
              <p className="px-3 pt-3 pb-2 font-semibold text-muted-foreground text-xs uppercase tracking-wider">
                {t("header.country")}
              </p>
              <CommandInput placeholder={t("header.searchCountries")} />
              <CommandList>
                <CommandEmpty>{t("header.noCountriesFound")}</CommandEmpty>
                <CommandGroup>
                  {countries.map(({ code, flag, name }) => {
                    const isSelected = code === country.code;

                    return (
                      <CommandItem
                        key={code}
                        value={name}
                        keywords={code ? [code] : undefined}
                        onSelect={() => handleSelect(code)}
                        className="gap-3"
                      >
                        <span aria-hidden="true">{flag}</span>
                        <span className={isSelected ? "font-semibold" : undefined}>{name}</span>
                        {isSelected && <Check className="ml-auto text-primary" />}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};
